import {Box, Paper, Typography} from "@mui/material";
import {Assessment as AnalyticsIcon, FitnessCenter, CheckCircle} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {getWorkout} from "../../services/PlanSerice";
import {targetMuscles} from "../../enums/TargetMuscles";

const WeekMusclesBreakdown = ({planState, reloadKey}) => {
    const [muscleBreakdown, setMuscleBreakdown] = useState({});
    const [weekStats, setWeekStats] = useState({
        totalSets: 0,
        totalExercises: 0,
        completedWorkouts: 0,
        totalWorkouts: 0
    });
    const [loading, setLoading] = useState(true);

    // Define muscle groupings with display names and sort order
    const muscleGroupings = {
        'LEGS_QUADS': {group: 'LEGS_QUADS', name: 'רגליים-שרשרת קדמית', order: 1},
        'LEGS_HAMSTRINGS': {group: 'LEGS_HAMSTRINGS', name: 'רגליים-שרשרת אחורית', order: 2},
        'LEGS_GLUTES': {group: 'LEGS_GLUTES', name: 'ישבן', order: 3},
        'LEGS_ADDUCTORS': {group: 'LEGS_GLUTES', name: 'ישבן', order: 3},
        'LEGS_ABDUCTORS': {group: 'LEGS_GLUTES', name: 'ישבן', order: 3},
        'LEGS_CALVES': {group: 'LEGS_CALVES', name: 'רגליים-תאומים', order: 4},
        'FEET': {group: 'FEET', name: 'כפות רגליים', order: 4.5},
        'BACK_ROWES': {group: 'BACK', name: 'גב', order: 5},
        'BACK_PULLS': {group: 'BACK', name: 'גב', order: 5},
        'BACK_LOWER_BACK': {group: 'BACK', name: 'גב', order: 5},
        'CHEST_UPPER': {group: 'CHEST', name: 'חזה', order: 6},
        'CHEST_MIDDLE': {group: 'CHEST', name: 'חזה', order: 6},
        'CHEST_LOWER': {group: 'CHEST', name: 'חזה', order: 6},
        'ARMS_BICEPS': {group: 'ARMS_BICEPS', name: 'ידיים-יד קדמית', order: 7},
        'ARMS_TRICEPS': {group: 'ARMS_TRICEPS', name: 'ידיים-יד אחורית', order: 8},
        'ARMS_FOREARMS': {group: 'ARMS_FOREARMS', name: 'ידיים-אמות', order: 9},
        'SHOULDERS_FRONT_DELTS': {group: 'SHOULDERS', name: 'כתפיים', order: 10},
        'SHOULDERS_LATERAL_DELTS': {group: 'SHOULDERS', name: 'כתפיים', order: 10},
        'SHOULDERS_REAR_DELTS': {group: 'SHOULDERS', name: 'כתפיים', order: 10},
        'SHOULDERS_TRAPS': {group: 'SHOULDERS_TRAPS', name: 'כתפיים-טרפזים', order: 11},
        'NECK': {group: 'NECK', name: 'צוואר', order: 11.5},
        'ABS_OBLIQUES': {group: 'ABS', name: 'בטן', order: 12},
        'ABS': {group: 'ABS', name: 'בטן', order: 12},
        'CARDIO': {group: 'CARDIO', name: 'קרדיו', order: 13},
    };

    useEffect(() => {
        const fetchAllWorkouts = async () => {
            if (!planState?.plan?.blocks) {
                setLoading(false);
                return;
            }

            const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
            const curBlock = blocks.at(planState.blockIdx);

            if (!curBlock?.weeks) {
                setLoading(false);
                return;
            }

            const weeks = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
            const curWeek = weeks.at(planState.weekIdx);

            if (!curWeek?.workouts) {
                setLoading(false);
                return;
            }

            try {
                // Fetch all workouts in the week
                const workoutPromises = curWeek.workouts.map(workout => getWorkout(workout.id));
                const workoutResponses = await Promise.all(workoutPromises);
                const allWorkouts = workoutResponses.map(res => res.data);

                // Calculate muscle breakdown
                const {breakdown, stats} = calculateMuscleBreakdown(allWorkouts, curWeek.workouts);
                setMuscleBreakdown(breakdown);
                setWeekStats(stats);
            } catch (error) {
                console.error("Error fetching workouts:", error);
            } finally {
                setLoading(false);
            }
        };

        setLoading(true);
        fetchAllWorkouts();
    }, [planState, reloadKey]);

    const calculateMuscleBreakdown = (workouts, weekWorkouts) => {
        const muscleData = {};
        let totalSets = 0;
        let totalExercises = 0;
        const exerciseIds = new Set();

        // Initialize all muscles with 0 sets
        targetMuscles.forEach(muscle => {
            muscleData[muscle.value] = {
                name: muscle.name,
                sets: 0,
                exercises: 0
            };
        });

        // Calculate sets for each muscle from all workouts
        workouts.forEach(workout => {
            workout.programExercises?.forEach(exercise => {
                if (exercise.targetMuscle && exercise.intervals) {
                    const muscle = exercise.targetMuscle;
                    const setsCount = exercise.intervals.length;

                    if (muscleData[muscle]) {
                        muscleData[muscle].sets += setsCount;
                        muscleData[muscle].exercises += 1;
                    }

                    totalSets += setsCount;

                    // Count unique exercises
                    if (exercise.exerciseId && !exerciseIds.has(exercise.exerciseId)) {
                        exerciseIds.add(exercise.exerciseId);
                        totalExercises++;
                    } else if (!exercise.exerciseId) {
                        totalExercises++;
                    }
                }
            });
        });

        // Count completed workouts
        const completedWorkouts = weekWorkouts.filter(w => w.stateType === "COMPLETED").length;

        // Group muscles according to muscleGroupings
        const groupedBreakdown = {};

        Object.entries(muscleData).forEach(([muscleKey, muscleInfo]) => {
            if (muscleInfo.sets > 0 && muscleGroupings[muscleKey]) {
                const grouping = muscleGroupings[muscleKey];
                const groupKey = grouping.group;

                if (!groupedBreakdown[groupKey]) {
                    groupedBreakdown[groupKey] = {
                        name: grouping.name,
                        sets: 0,
                        exercises: 0,
                        order: grouping.order
                    };
                }

                groupedBreakdown[groupKey].sets += muscleInfo.sets;
                groupedBreakdown[groupKey].exercises += muscleInfo.exercises;
            }
        });

        return {
            breakdown: groupedBreakdown,
            stats: {
                totalSets,
                totalExercises,
                completedWorkouts,
                totalWorkouts: weekWorkouts.length
            }
        };
    };

    const hasMuscleData = Object.keys(muscleBreakdown).length > 0;

    if (loading) {
        return null;
    }

    return (
        <Paper
            sx={{
                backgroundColor: "#FCFCFD",
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                direction: "rtl"
            }}
            elevation={0}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 16px",
                    backgroundColor: "#F8FAFC",
                    borderBottom: "1px solid #E2E8F0"
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                    <AnalyticsIcon sx={{color: "#64748B", fontSize: "16px"}}/>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#1E293B",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        סיכום שבועי
                    </Typography>
                </Box>
            </Box>

            {/* Week Summary Stats */}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 2, md: 3 },
                    padding: { xs: "10px 12px", md: "12px 16px" },
                    borderBottom: "1px solid #E2E8F0",
                    backgroundColor: "#FAFBFC"
                }}
            >
                <StatItem
                    icon={<FitnessCenter sx={{fontSize: "14px"}}/>}
                    label="סה״כ סטים"
                    value={weekStats.totalSets}
                />
                <StatItem
                    icon={<AnalyticsIcon sx={{fontSize: "14px"}}/>}
                    label="תרגילים"
                    value={weekStats.totalExercises}
                />
                <StatItem
                    icon={<CheckCircle sx={{fontSize: "14px"}}/>}
                    label="אימונים שהושלמו"
                    value={`${weekStats.completedWorkouts}/${weekStats.totalWorkouts}`}
                    highlight={weekStats.completedWorkouts === weekStats.totalWorkouts && weekStats.totalWorkouts > 0}
                />
            </Box>

            {/* Muscle Breakdown */}
            <Box sx={{padding: { xs: "10px 12px", md: "12px 16px" }}}>
                <Typography
                    sx={{
                        fontSize: { xs: "11px", md: "12px" },
                        fontWeight: 600,
                        color: "#64748B",
                        mb: 1
                    }}
                >
                    נפח סטים לפי שריר
                </Typography>
                {!hasMuscleData ? (
                    <Typography
                        sx={{
                            fontSize: "12px",
                            color: "#94A3B8",
                            textAlign: "center",
                            py: 1
                        }}
                    >
                        אין נתונים
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: { xs: "6px", md: "8px" }
                        }}
                    >
                        {Object.entries(muscleBreakdown)
                            .sort(([, a], [, b]) => b.sets - a.sets)
                            .map(([muscleKey, muscleData]) => (
                                <MuscleChip
                                    key={muscleKey}
                                    muscle={muscleData.name}
                                    sets={muscleData.sets}
                                    exercises={muscleData.exercises}
                                />
                            ))}
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

const StatItem = ({icon, label, value, highlight}) => {
    return (
        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
            <Box sx={{color: highlight ? "#22C55E" : "#64748B"}}>
                {icon}
            </Box>
            <Box>
                <Typography
                    sx={{
                        fontSize: "11px",
                        color: "#94A3B8",
                        lineHeight: 1
                    }}
                >
                    {label}
                </Typography>
                <Typography
                    sx={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: highlight ? "#22C55E" : "#1E293B",
                        lineHeight: 1.2
                    }}
                >
                    {value}
                </Typography>
            </Box>
        </Box>
    );
};

const MuscleChip = ({muscle, sets, exercises}) => {
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: { xs: 0.5, md: 0.75 },
                padding: { xs: "4px 8px", md: "6px 12px" },
                backgroundColor: "#F1F5F9",
                borderRadius: "4px",
                border: "1px solid #E2E8F0",
                transition: "all 0.2s",
                "&:hover": {
                    backgroundColor: "#E2E8F0",
                    transform: "translateY(-1px)"
                }
            }}
        >
            <Typography
                sx={{
                    fontSize: { xs: "11px", md: "13px" },
                    fontWeight: 500,
                    color: "#64748B",
                    lineHeight: 1.2
                }}
            >
                {muscle}
            </Typography>
            <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                <Typography
                    sx={{
                        fontSize: { xs: "12px", md: "14px" },
                        fontWeight: 700,
                        color: "#1E293B",
                        lineHeight: 1
                    }}
                >
                    {sets}
                </Typography>
                <Typography
                    sx={{
                        fontSize: { xs: "9px", md: "11px" },
                        color: "#94A3B8",
                        lineHeight: 1,
                        display: { xs: "none", sm: "block" }
                    }}
                >
                    ({exercises} תרגילים)
                </Typography>
            </Box>
        </Box>
    );
};

export default WeekMusclesBreakdown;
